// Where a new account came from: an Instagram ad, a Google search, a link
// someone shared. The browser remembers the visit that brought someone to
// the site, and sign-up saves it on the account so the admin Users page can
// show it. It never leaves this site: the API keeps it, no tracker sees it.
//
// The backend's signup_source.py turns what is saved here into the label the
// Users page shows ("Instagram ad"). This file only records; it never labels.
// authStore sends it, once, after a sign-up.

const KEY = 'nhd_first_visit';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const MAX = 200;

/**
 * Instagram's and Facebook's own in-app browsers say so in their user agent.
 * Ad clicks open in them, so this still tells Instagram from Facebook when a
 * link carries no utm tags.
 */
export function inAppBrowser(userAgent = '') {
  if (/Instagram/i.test(userAgent)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/.test(userAgent)) return 'facebook';
  return null;
}

/** The host a referrer came from, or null for none or for this site itself. */
export function referrerHost(referrer, ownHost) {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host && host !== ownHost ? host : null;
  } catch {
    return null;
  }
}

/** What this page load says about where the visitor came from. */
export function visitFrom({ search = '', referrer = '', userAgent = '', path = '/', ownHost = '', now = new Date() }) {
  const params = new window.URLSearchParams(search);
  const visit = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) visit[key] = value.slice(0, MAX);
  });
  const host = referrerHost(referrer, ownHost);
  if (host) visit.referrer_host = host;
  const app = inAppBrowser(userAgent);
  if (app) visit.in_app = app;
  visit.landing_path = path.slice(0, MAX);
  visit.first_seen_at = now.toISOString();
  return visit;
}

const tagged = (visit) => Boolean(visit?.utm_source);

/**
 * The first visit wins, so someone who clicks an ad and comes back days later
 * by typing the address is still counted as the ad. The one exception: a
 * tagged visit replaces an untagged one, because the tags say more.
 */
export function chooseVisit(stored, current) {
  if (!stored) return current;
  if (!tagged(stored) && tagged(current)) return current;
  return stored;
}

/** The remembered visit, or null. */
export function rememberedVisit() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || null;
  } catch {
    return null;
  }
}

/** Run once per page load, before anything changes the URL. */
export function rememberVisit() {
  try {
    const current = visitFrom({
      search: window.location.search,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      path: window.location.pathname,
      ownHost: window.location.hostname.toLowerCase(),
    });
    const stored = rememberedVisit();
    const chosen = chooseVisit(stored, current);
    if (chosen !== stored) localStorage.setItem(KEY, JSON.stringify(chosen));
  } catch {
    // Storage refused (a private window): the account just has no source
  }
}
